import logger from '@eventbrite/client-logger';
import { fetchV3WithTranslateServerErrors } from './base';
import { _validateTaxIdCountry, _validateQstTaxIdCountry } from './constants';
import { EVENT_DEFAULT_ERROR_MESSAGE } from '../constants';

const ERROR_SPEC = {
    default: () => EVENT_DEFAULT_ERROR_MESSAGE,
};

const DEFAULT_VALIDATE_TAX_ID_TIMEOUT_IN_MILLISECONDS = 3000;

/**
 * @param {string} eventId
 * @param {string} country
 * @param {string} taxId
 * @param [object] options
 * @param [number] options.timeoutInMilliseconds
 * @returns {Promise<{ is_tax_exempt: boolean; valid: boolean; }>}
 */
export const validateTaxId = (
    eventId,
    country,
    taxId,
    {
        timeoutInMilliseconds = DEFAULT_VALIDATE_TAX_ID_TIMEOUT_IN_MILLISECONDS,
    } = {},
) =>
    new Promise((resolve, reject) => {
        const endpoint = _validateTaxIdCountry(eventId, country, taxId);

        const timeoutToken = setTimeout(() => {
            logger.warn(
                'validateTaxId took too long to respond, assuming taxes are valid',
                {
                    endpoint,
                    eventId,
                    country,
                    taxId,
                    timeoutInMilliseconds,
                },
            );

            resolve({
                is_tax_exempt: true,
                valid: true,
            });
        }, timeoutInMilliseconds);

        fetchV3WithTranslateServerErrors(ERROR_SPEC, endpoint, {
            method: 'GET',
        })
            .then(({ response }) => resolve(response))
            .catch(reject)
            .finally(() => clearTimeout(timeoutToken));
    });

/**
 * @param {object} params
 * @param {string} params.eventId
 * @param {string} params.residenceZipCode
 * @param {string} params.billingZipCode
 * @param {string} params.taxId
 * @param [object] options
 * @param [number] options.timeoutInMilliseconds
 * @returns {Promise<{ is_tax_exempt: boolean; valid: boolean; }>}
 */
export const validateQstTaxId = (
    { eventId, residenceZipCode, billingZipCode, taxId },
    {
        timeoutInMilliseconds = DEFAULT_VALIDATE_TAX_ID_TIMEOUT_IN_MILLISECONDS,
    } = {},
) =>
    new Promise((resolve, reject) => {
        const endpoint = _validateQstTaxIdCountry();

        const timeoutToken = setTimeout(() => {
            logger.warn(
                'validateQstTaxId took too long to respond, assuming taxes are invalid',
                {
                    endpoint,
                    eventId,
                    residenceZipCode,
                    billingZipCode,
                    taxId,
                    timeoutInMilliseconds,
                },
            );

            resolve({
                is_exempt: false,
                is_tax_id_valid: false,
                is_zip_code_valid: false,
            });
        }, timeoutInMilliseconds);

        const body = JSON.stringify({
            event_id: eventId,
            zip_code_tax_residence: residenceZipCode,
            billing_zip_code: billingZipCode,
            tax_id: taxId,
        });

        fetchV3WithTranslateServerErrors(ERROR_SPEC, endpoint, {
            method: 'POST',
            body,
        })
            .then(({ response }) => resolve(response))
            .catch((error) => {
                logger.error(
                    'validateQstTaxId thrown an error, assuming taxes are invalid',
                    {
                        endpoint,
                        eventId,
                        residenceZipCode,
                        billingZipCode,
                        taxId,
                        error,
                    },
                );

                const { parsedError } = error.extraData;
                resolve(
                    parsedError.error === 'ARGUMENTS_ERROR'
                        ? {
                              is_exempt: false,
                              is_tax_id_valid:
                                  !parsedError.argumentErrors.tax_id,
                              is_zip_code_valid:
                                  !parsedError.argumentErrors
                                      .zip_code_tax_residence,
                          }
                        : {
                              is_exempt: false,
                              is_tax_id_valid: false,
                              is_zip_code_valid: false,
                          },
                );
            })
            .finally(() => clearTimeout(timeoutToken));
    });
